import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Clock, DollarSign } from "lucide-react";
import { motion } from "framer-motion";

const ROICalculator = () => {
  const [hoursPerProposal, setHoursPerProposal] = useState(3);
  const [hourlyRate, setHourlyRate] = useState(50);
  const [proposalsPerMonth, setProposalsPerMonth] = useState(8);

  const sentinelMinutes = 15;
  const oldTotalHours = hoursPerProposal * proposalsPerMonth;
  const newTotalHours = (sentinelMinutes / 60) * proposalsPerMonth;
  const hoursSaved = Math.max(0, oldTotalHours - newTotalHours);
  const dollarsSaved = hoursSaved * hourlyRate;

  return (
    <section className="py-20 md:py-28 border-t border-border/50">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            See How Much You&apos;ll{" "}
            <span className="text-gradient">Save</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Calculate the time and money you save every month with automated proposals.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <Card className="border-border bg-card shadow-card">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div>
                  <Label htmlFor="roi-hours" className="text-muted-foreground text-sm mb-2 block">Hours spent per proposal</Label>
                  <Input
                    id="roi-hours"
                    type="number"
                    min={0.5}
                    step={0.5}
                    value={hoursPerProposal}
                    onChange={(e) => setHoursPerProposal(Number(e.target.value) || 0)}
                    className="bg-secondary border-border text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="roi-rate" className="text-muted-foreground text-sm mb-2 block">Your hourly rate ($)</Label>
                  <Input
                    id="roi-rate"
                    type="number"
                    min={1}
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value) || 0)}
                    className="bg-secondary border-border text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="roi-proposals" className="text-muted-foreground text-sm mb-2 block">Proposals per month</Label>
                  <Input
                    id="roi-proposals"
                    type="number"
                    min={1}
                    value={proposalsPerMonth}
                    onChange={(e) => setProposalsPerMonth(Number(e.target.value) || 0)}
                    className="bg-secondary border-border text-foreground"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
                  <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-bold text-foreground">{hoursSaved.toFixed(1)} hrs</p>
                  <p className="text-sm text-muted-foreground mt-1">saved per month</p>
                </div>
                <div className="rounded-xl border border-success/20 bg-success/5 p-6 text-center">
                  <DollarSign className="h-8 w-8 text-success mx-auto mb-3" />
                  <p className="text-3xl font-bold text-foreground">${dollarsSaved.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground mt-1">saved per month</p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-6">
                Based on ~15 minutes per proposal with SentinelForge vs. {hoursPerProposal} hours manually.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator;
